import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

document.addEventListener('DOMContentLoaded', () => {
    const form = document.getElementById('concierge-event-guest-import-form')

    if (!form) {
        return
    }

    const fileInput = form.querySelector('input[type="file"]')
    const modalEl = document.getElementById('conciergeEventGuestImportModal')

    if (!fileInput || !modalEl) {
        return
    }

    const state = {
        total: 0,
        processed: 0,
        imported: 0,
        errors: 0,
        importId: null,
        finalizeUrl: null,
        failuresDownloadUrl: null,
        echoChannel: null,
        isRunning: false,
    }

    const elements = {
        statusText: modalEl.querySelector('#completion-message'),
        progressBar: modalEl.querySelector('#import-progress-bar'),
        progressPercent: modalEl.querySelector('#import-progress-percent'),
        importedCount: modalEl.querySelector('#imported-count'),
        errorsCount: modalEl.querySelector('#errors-count'),
        errorsList: modalEl.querySelector('#error-list-body'),
        errorsSection: modalEl.querySelector('#error-list-section'),
        closeButton: modalEl.querySelector('#closeImportModalFooter'),
        downloadFailuresBtn: modalEl.querySelector('#download-failures-btn'),
        downloadFailuresSection: modalEl.querySelector('#download-failures-section'),
        progressSection: modalEl.querySelector('#import-progress-section'),
        completionSection: modalEl.querySelector('#import-completion-section'),
        waitNotice: modalEl.querySelector('#import-wait-notice'),
    }

    const i18n = JSON.parse(modalEl.dataset.i18n || '{}')

    fileInput.addEventListener('change', handleFileChange)

    function handleFileChange() {
        if (!fileInput.files || fileInput.files.length === 0) {
            return
        }

        startImport(fileInput.files[0])
    }

    async function startImport(file) {
        resetModal()
        openModal()
        bindBeforeUnload()

        const formData = new FormData()
        formData.append('csvImport', file)

        try {
            const response = await fetch(form.action, {
                method: 'POST',
                headers: buildHeaders(),
                credentials: 'same-origin',
                body: formData,
            })

            if (!response.ok) {
                const payload = await readJsonSafely(response)
                let errorMessage = i18n.failed

                if (payload) {
                    if (payload.message) {
                        errorMessage = payload.message
                    } else if (payload.errors) {
                        errorMessage = Object.values(payload.errors).flat().join(', ')
                    } else if (typeof payload === 'string') {
                        errorMessage = payload
                    }
                }

                handleFailure(errorMessage)
                return
            }

            const payload = await response.json()
            state.importId = payload.importId
            state.total = payload.total
            state.finalizeUrl = payload.finalizeUrl
            state.failuresDownloadUrl = payload.failuresDownloadUrl

            updateProgress(0)
            showWaitNotice()
            subscribeToChannel()
        } catch (error) {
            console.error('Concierge event guest import failed to start', error)
            handleFailure(i18n.failed)
        }
    }

    function subscribeToChannel() {
        ensureEchoInitialized()

        const tenantId = globalThis.__TENANT_ID__

        if (!tenantId || !globalThis.Echo) {
            handleFailure(i18n.failed)

            return
        }

        const channelName = `concierge-event-guest-import.${tenantId}.${state.importId}`
        state.echoChannel = globalThis.Echo.private(channelName)
        state.echoChannel.listen('.progress', handleProgress)
        state.echoChannel.listen('.row-failed', handleRowFailed)
        state.echoChannel.listen('.completed', handleCompleted)
    }

    function handleProgress(event) {
        const { processed, total, imported, errors } = event
        state.processed = processed
        state.total = total
        state.imported = imported
        state.errors = errors
        updateProgress()
    }

    function handleRowFailed(event) {
        const { row_number: rowNumber, first_name: firstName, last_name: lastName, messages } = event
        const message = Array.isArray(messages) ? messages.join(' | ') : String(messages ?? '')
        const hasName = Boolean((firstName && firstName.length > 0) || (lastName && lastName.length > 0))

        if (elements.errorsSection?.classList.contains('d-none')) {
            elements.errorsSection.classList.remove('d-none')
        }

        const tr = document.createElement('tr')

        const rowTd = document.createElement('td')
        rowTd.textContent = String(rowNumber)
        tr.appendChild(rowTd)

        if (hasName) {
            const nameTd = document.createElement('td')
            nameTd.textContent = `${firstName ?? ''} ${lastName ?? ''}`.trim()
            tr.appendChild(nameTd)
        } else {
            const emptyTd = document.createElement('td')
            emptyTd.textContent = '-'
            tr.appendChild(emptyTd)
        }

        const messageTd = document.createElement('td')
        messageTd.textContent = message
        tr.appendChild(messageTd)

        elements.errorsList.appendChild(tr)
    }

    function handleCompleted(event) {
        const { imported, errors, total, failures_download_url: failuresUrl, redirect_url: redirectUrl, failed } = event

        state.imported = imported
        state.errors = errors
        state.total = total || state.total
        state.processed = state.total
        state.finalizeUrl = redirectUrl || state.finalizeUrl
        state.failuresDownloadUrl = failuresUrl || state.failuresDownloadUrl

        updateProgress()
        hideWaitNotice()

        if (failed) {
            showErrorState(i18n.failed)
        } else {
            showSuccessState()
        }

        enableClose()
        releaseBeforeUnload()
    }

    function updateProgress(percent) {
        let ratio
        if (typeof percent === 'number') {
            ratio = percent === 0 ? 1 : percent
        } else if (state.total > 0) {
            ratio = Math.min(100, Math.max(1, Math.round((state.processed / state.total) * 100)))
        } else {
            ratio = 1
        }

        if (elements.progressBar) {
            elements.progressBar.style.width = `${ratio}%`
            elements.progressBar.setAttribute('aria-valuenow', String(ratio))
            elements.progressBar.textContent = `${ratio}%`
        }

        if (elements.progressPercent) {
            elements.progressPercent.classList.remove('d-none')
            elements.progressPercent.textContent = `(${ratio}%)`
        }

        const processedEl = modalEl.querySelector('#processed-count')
        const totalEl = modalEl.querySelector('#total-count')
        if (processedEl) {
            processedEl.textContent = String(state.processed)
        }
        if (totalEl) {
            totalEl.textContent = String(state.total)
        }

        if (elements.importedCount) {
            elements.importedCount.textContent = String(state.imported)
        }

        if (elements.errorsCount) {
            elements.errorsCount.textContent = String(state.errors)
        }
    }

    function showSuccessState() {
        if (elements.progressBar) {
            elements.progressBar.style.width = '100%'
            elements.progressBar.classList.remove('progress-bar-striped', 'progress-bar-animated', 'bg-info')
            elements.progressBar.classList.add('bg-success')
            elements.progressBar.textContent = i18n.completed
        }

        elements.completionSection?.classList.remove('d-none')

        if (elements.statusText) {
            if (state.errors > 0) {
                const message = (i18n.completion_with_errors || '')
                    .replace(':imported', state.imported)
                    .replace(':errors', state.errors)
                elements.statusText.className = 'alert alert-outline alert-success'
                elements.statusText.textContent = message
            } else {
                const message = (i18n.completion_no_errors || '')
                    .replace(':imported', state.imported)
                elements.statusText.className = 'alert alert-outline alert-success'
                elements.statusText.textContent = message
            }
        }

        if (state.errors > 0 && state.failuresDownloadUrl && elements.downloadFailuresBtn) {
            elements.downloadFailuresBtn.href = state.failuresDownloadUrl
            elements.downloadFailuresSection?.classList.remove('d-none')
        }

        if (state.errors > 0 && elements.errorsSection) {
            elements.errorsSection.classList.remove('d-none')
        }
    }

    function showErrorState(text) {
        elements.progressSection?.classList.add('d-none')
        elements.completionSection?.classList.remove('d-none')

        if (elements.statusText) {
            elements.statusText.className = 'alert alert-outline alert-danger'
            elements.statusText.textContent = text || i18n.failed
        }

        if (state.errors > 0 && state.failuresDownloadUrl && elements.downloadFailuresBtn) {
            elements.downloadFailuresBtn.href = state.failuresDownloadUrl
            elements.downloadFailuresSection?.classList.remove('d-none')
        }

        if (state.errors > 0 && elements.errorsSection) {
            elements.errorsSection.classList.remove('d-none')
        }
    }

    function handleFailure(text) {
        releaseBeforeUnload()
        hideWaitNotice()
        showErrorState(text)
        enableClose(true)
    }

    function enableClose(dismissOnly = false) {
        if (!elements.closeButton) {
            return
        }

        elements.closeButton.classList.remove('d-none')
        elements.closeButton.disabled = false

        elements.closeButton.onclick = event => {
            event.preventDefault()
            releaseBeforeUnload()

            if (dismissOnly || !state.finalizeUrl) {
                closeModal()
                resetFileInput()

                return
            }

            globalThis.location.href = state.finalizeUrl
        }
    }

    function openModal() {
        state.isRunning = true

        if (window.jQuery) {
            window.jQuery(modalEl).modal({ backdrop: 'static', keyboard: false })
            window.jQuery(modalEl).modal('show')
        } else {
            modalEl.classList.add('show', 'd-block')
            modalEl.setAttribute('aria-hidden', 'false')
        }

        modalEl.addEventListener('hidden.bs.modal', onModalHidden, { once: true })
    }

    function closeModal() {
        state.isRunning = false

        if (window.jQuery) {
            window.jQuery(modalEl).modal('hide')
        } else {
            modalEl.classList.remove('show', 'd-block')
            modalEl.setAttribute('aria-hidden', 'true')
        }
    }

    function showWaitNotice() {
        elements.waitNotice?.classList.remove('d-none')
    }

    function hideWaitNotice() {
        elements.waitNotice?.classList.add('d-none')
    }

    function onModalHidden() {
        hideWaitNotice()
        elements.completionSection?.classList.add('d-none')

        if (elements.statusText) {
            elements.statusText.className = 'alert'
            elements.statusText.textContent = ''
        }
    }

    function resetModal() {
        state.total = 0
        state.processed = 0
        state.imported = 0
        state.errors = 0
        state.importId = null
        state.finalizeUrl = null
        state.failuresDownloadUrl = null

        if (elements.errorsList) {
            elements.errorsList.innerHTML = ''
        }

        elements.errorsSection?.classList.add('d-none')
        elements.downloadFailuresSection?.classList.add('d-none')
        hideWaitNotice()

        if (elements.progressPercent) {
            elements.progressPercent.classList.add('d-none')
            elements.progressPercent.textContent = ''
        }

        if (elements.closeButton) {
            elements.closeButton.classList.add('d-none')
            elements.closeButton.disabled = true
            elements.closeButton.onclick = null
        }

        elements.progressSection?.classList.remove('d-none')
        elements.completionSection?.classList.add('d-none')

        if (elements.statusText) {
            elements.statusText.className = 'alert'
            elements.statusText.textContent = ''
        }

        updateProgress(0)
    }

    function resetFileInput() {
        fileInput.value = ''
    }

    function bindBeforeUnload() {
        window.addEventListener('beforeunload', onBeforeUnload)
    }

    function releaseBeforeUnload() {
        state.isRunning = false
        window.removeEventListener('beforeunload', onBeforeUnload)
    }

    function onBeforeUnload(event) {
        if (!state.isRunning) {
            return
        }

        event.preventDefault()
        event.returnValue = i18n.leave_warning
    }

    function ensureEchoInitialized() {
        if (globalThis.Echo) {
            return
        }

        if (!globalThis.PUSHER_KEY) {
            console.error('PUSHER_KEY not configured; import progress cannot be tracked.')

            return
        }

        globalThis.Pusher = Pusher

        globalThis.Echo = new Echo({
            broadcaster: 'pusher',
            key: globalThis.PUSHER_KEY,
            cluster: globalThis.PUSHER_CLUSTER,
            forceTLS: true,
            authEndpoint: '/concierge/broadcasting/auth',
            auth: {
                headers: {
                    'X-CSRF-TOKEN': getCsrfToken(),
                    Accept: 'application/json',
                },
            },
        })
    }

    function buildHeaders() {
        return {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': getCsrfToken(),
        }
    }

    function getCsrfToken() {
        const meta = document.querySelector('meta[name="csrf-token"]')

        return meta ? meta.getAttribute('content') : ''
    }

    async function readJsonSafely(response) {
        try {
            return await response.json()
        } catch (error) {
            return null
        }
    }
})
